import React, { useState } from "react";
import { Link } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../config/firebase";
import styles from "./LetsGoWaitlist.module.css";

const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.logoIcon}>
    <path d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={styles.successIcon}>
    <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const features = [
  {
    icon: "🗺️",
    title: "Discover Nearby Events",
    description: "See what's happening around you right now — concerts, markets, pop-ups, and more.",
  },
  {
    icon: "📸",
    title: "Rich Event Details",
    description: "Photo galleries, maps, organizer info, and attendee counts — everything you need.",
  },
  {
    icon: "📅",
    title: "One-Tap Calendar",
    description: "Save any event to your iPhone calendar without leaving the app.",
  },
  {
    icon: "❤️",
    title: "Save Favorites",
    description: "Bookmark events you're interested in and revisit them any time.",
  },
];

type FormState = "idle" | "loading" | "success" | "error";

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const LetsGoWaitlist: React.FC = () => {
  const [email, setEmail] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [emailError, setEmailError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setFormState("loading");
    try {
      await addDoc(collection(db, "letsgo-waitlist"), {
        email: trimmed,
        submittedAt: serverTimestamp(),
      });
      setFormState("success");
    } catch {
      setFormState("error");
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.backgroundGlow} />

      <nav className={styles.nav}>
        <div className={styles.navContainer}>
          <Link to="/lets-go" className={styles.logo}>
            <LogoIcon />
            <span>Let's Go</span>
          </Link>
        </div>
      </nav>

      <main id="main-content" className={styles.main} role="main" tabIndex={-1}>
        <div className={styles.container}>
          <div className={styles.badge}>Coming Soon to iOS</div>

          <h1 className={styles.headline}>
            Your city is buzzing.<br />Be the first to know.
          </h1>
          <p className={styles.subheadline}>
            Let's Go helps you discover concerts, markets, pop-ups, and local events near you — with full details, maps, and one-tap calendar saving.
          </p>

          {formState === "success" ? (
            <div className={styles.successCard}>
              <CheckCircleIcon />
              <h2 className={styles.successTitle}>You're on the list!</h2>
              <p className={styles.successText}>
                We'll send you one email when Let's Go launches on iOS. No spam, ever.
              </p>
              <Link to="/lets-go" className={styles.backLink}>
                Back to Let's Go
              </Link>
            </div>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.inputRow}>
                <div className={styles.inputWrapper}>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    className={`${styles.input} ${emailError ? styles.inputError : ""}`}
                    aria-label="Email address"
                    aria-describedby={emailError ? "email-error" : undefined}
                    disabled={formState === "loading"}
                    autoComplete="email"
                  />
                  {emailError && (
                    <p id="email-error" className={styles.errorText} role="alert">
                      {emailError}
                    </p>
                  )}
                </div>
                <button type="submit" className={styles.submitButton} disabled={formState === "loading"}>
                  {formState === "loading" ? (
                    <span className={styles.spinner} aria-hidden="true" />
                  ) : (
                    "Notify Me"
                  )}
                </button>
              </div>

              <p className={styles.consent}>
                By signing up you agree to receive one launch notification. No spam. Unsubscribe by emailing{" "}
                <a href="mailto:dixonappdevelopment@gmail.com" className={styles.consentLink}>
                  dixonappdevelopment@gmail.com
                </a>
                .
              </p>

              {formState === "error" && (
                <p className={styles.errorText} role="alert">
                  Something went wrong. Please try again or email us directly.
                </p>
              )}
            </form>
          )}

          <div className={styles.features}>
            {features.map((f) => (
              <div key={f.title} className={styles.featureCard}>
                <div className={styles.featureEmoji}>{f.icon}</div>
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureDescription}>{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className={styles.footer}>
        <p>
          &copy; {new Date().getFullYear()} Dixon App Development &middot;{" "}
          <Link to="/" className={styles.footerLink}>
            Dixon App Development
          </Link>
        </p>
      </footer>
    </div>
  );
};
